import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { menuSections } from './menuItems';
import useAuth from '../../hooks/useAuth';
import useTenant from '../../hooks/useTenant';
import useCapabilities from '../../hooks/useCapabilities';
import { getImpersonado } from '../../utils/impersonation';

export const DRAWER_WIDTH = 270;

// Roles de backoffice — usam o konq-backoffice pra tudo; aqui só veem os itens
// neutros (sem `forTipo`) até decidirem impersonar alguém.
const BACKOFFICE_ROLES = ['Master', 'Admin', 'Curador', 'Analista'];

/** Filtro de visibilidade dos itens do menu (regras em menuItems.jsx). */
function useItemVisivel() {
  const { hasCapability } = useCapabilities();
  const { bootstrap, roleTenant } = useTenant();
  const { roleGlobal } = useAuth();
  const concursoAtivo = bootstrap?.concursoAtivo;
  const isBackofficeRole = roleGlobal === 'Master' || BACKOFFICE_ROLES.includes(roleTenant);
  const imp = getImpersonado();

  return (item) => {
    if (item.requireBootstrapFlag && concursoAtivo?.[item.requireBootstrapFlag] !== true) return false;
    if (isBackofficeRole && !imp) return !item.forTipo;
    if (imp) return !item.forTipo || item.forTipo.includes(imp.tipo);
    return !item.capability || hasCapability(item.capability);
  };
}

export function iniciais(nome) {
  return (nome || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n.charAt(0))
    .join('')
    .toUpperCase();
}

export default function Sidebar({ open, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { branding, currentTenant } = useTenant();
  const { isLoaded } = useCapabilities();
  const itemVisivel = useItemVisivel();
  const imp = getImpersonado();

  const tenantNome = branding?.nome || currentTenant?.nome;
  const nomeUsuario = imp?.nome || user?.nome;

  const allPaths = menuSections.flatMap((s) => s.items.map((i) => i.path));
  const isActive = (path) => {
    const maisEspecifico = allPaths.some((p) => p !== path && p.startsWith(path) && location.pathname.startsWith(p));
    if (maisEspecifico) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  // Com branding, sidebar invertida: fundo na cor primária do tenant e texto branco.
  const dark = !!branding?.corPrimaria;
  const bg = dark ? branding.corPrimaria : undefined;
  const txt = dark ? '#ffffff' : undefined;
  const txtMuted = dark ? 'rgba(255,255,255,0.6)' : 'text.secondary';
  const dividerSx = dark ? { borderColor: 'rgba(255,255,255,0.12)' } : undefined;
  const logoSrc = dark ? branding?.logoInvertidoUrl || branding?.logoUrl : branding?.logoUrl || currentTenant?.logoUrl;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: bg, color: txt }}>
      {/* Marca */}
      <Box onClick={() => handleNavigate('/dashboard')} sx={{ px: 2.5, py: 3, textAlign: 'center', cursor: 'pointer' }}>
        {logoSrc ? (
          <Box
            component="img"
            src={logoSrc}
            alt={tenantNome || 'Logo'}
            sx={{ maxHeight: 56, maxWidth: '100%', objectFit: 'contain', mb: 0.5 }}
          />
        ) : (
          <Typography
            sx={{ fontSize: 28, fontWeight: 700, color: txt ?? '#1A1A1A', lineHeight: 1, mb: 0.5, letterSpacing: -0.5 }}
          >
            konq
          </Typography>
        )}
        <Typography
          sx={{ color: txtMuted, letterSpacing: 2, textTransform: 'uppercase', fontSize: 9, fontWeight: 700 }}
        >
          {t('Contests')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
          <Typography sx={{ color: dark ? 'rgba(255,255,255,0.3)' : 'text.disabled', fontSize: 10 }}>by</Typography>
          <Box
            component="img"
            src="/assets/logo-hzero.png"
            alt="hzero"
            sx={{ height: 10, ...(dark && { filter: 'brightness(0) invert(1)', opacity: 0.6 }) }}
          />
        </Box>
      </Box>

      <Divider sx={dividerSx} />

      {/* Usuário */}
      <Box
        onClick={() => user && handleNavigate(`/perfil/detalhe/${user.clerkUserId}`)}
        sx={{
          mx: 2,
          my: 2,
          p: 2,
          bgcolor: dark ? 'rgba(255,255,255,0.06)' : 'grey.50',
          borderRadius: 2,
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            src={imp ? undefined : user?.imageUrl || undefined}
            sx={{
              width: 36,
              height: 36,
              fontSize: 14,
              bgcolor: dark ? 'rgba(255,255,255,0.2)' : 'primary.main',
              color: dark ? '#fff' : 'primary.contrastText',
            }}
          >
            {iniciais(nomeUsuario) || 'U'}
          </Avatar>
          <Typography variant="subtitle2" noWrap color={txt} sx={{ minWidth: 0 }}>
            {nomeUsuario || t('User')}
          </Typography>
        </Box>
      </Box>

      {/* Seções do menu — esqueleto até as capabilities carregarem
          (senão o item aparece e some logo depois). */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {!isLoaded && (
          <Box sx={{ px: 2, pt: 2 }}>
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} height={32} sx={{ my: 0.5, ...(dark && { bgcolor: 'rgba(255,255,255,0.12)' }) }} />
            ))}
          </Box>
        )}
        {isLoaded &&
          menuSections.map((section) => {
            const itens = section.items.filter(itemVisivel);
            if (itens.length === 0) return null;
            return (
              <Box key={section.title} sx={{ mb: 1 }}>
                <Typography
                  variant="overline"
                  sx={{
                    px: 2,
                    pt: 2,
                    pb: 0.5,
                    display: 'block',
                    color: txtMuted,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: 1.2,
                  }}
                >
                  {t(section.title)}
                </Typography>
                <List disablePadding>
                  {itens.map((item) => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    return (
                      <ListItemButton
                        key={item.path}
                        selected={active}
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          py: 0.75,
                          minHeight: 40,
                          ...(dark && {
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                            '&.Mui-selected': {
                              'bgcolor': 'rgba(255,255,255,0.18)',
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
                            },
                          }),
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 36,
                            color: dark
                              ? active
                                ? '#ffffff'
                                : 'rgba(255,255,255,0.7)'
                              : active
                                ? 'primary.main'
                                : 'text.secondary',
                          }}
                        >
                          <Icon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={t(item.label)}
                          primaryTypographyProps={{
                            fontSize: 13,
                            fontWeight: active ? 600 : 400,
                            color: dark ? '#ffffff' : active ? 'primary.main' : 'text.primary',
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Box>
            );
          })}
      </Box>

      {/* Rodapé — versão · build (o build vem do CI) */}
      <Divider sx={dividerSx} />
      <Box sx={{ px: 2.5, py: 1.5 }}>
        <Typography variant="caption" color={dark ? 'rgba(255,255,255,0.4)' : 'text.disabled'}>
          Konq v{__APP_VERSION__}
        </Typography>
      </Box>
    </Box>
  );

  const paperSx = {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    ...(dark
      ? { bgcolor: bg, borderRight: 'none' }
      : { bgcolor: 'background.paper', borderRight: '1px dashed', borderColor: 'divider' }),
  };

  return (
    <>
      <Drawer variant="permanent" sx={{ 'display': { xs: 'none', md: 'block' }, '& .MuiDrawer-paper': paperSx }}>
        {drawerContent}
      </Drawer>
      <Drawer
        variant="temporary"
        open={open}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{ 'display': { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': paperSx }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
